/**
 * FE-14: Growth Tab -- DAU, New Users, Messages, Summary Requests.
 * DatePresets + KPI cards + TrendLineChart + collapsible daily table.
 */

import { useCallback, useEffect, useState } from "react";
import { fetchGrowthMetrics } from "../../api/admin-metrics";
import type { GrowthMetrics } from "../../api/admin-metrics";
import { Td, Th, tableStyles } from "../../components/DataTable";
import { DatePresets } from "../../components/DatePresets";
import { TrendLineChart } from "../../components/charts/TrendLineChart";
import { t } from "../../i18n";
import * as shared from "../../styles/shared";
import { fmt } from "./helpers";

function defaultRange(): { start_date: string; end_date: string } {
  const end = new Date().toISOString().slice(0, 10);
  const start = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  return { start_date: start, end_date: end };
}

export function GrowthTab({
  highlight,
}: {
  highlight?: string;
}): React.JSX.Element {
  const [range, setRange] = useState(defaultRange);
  const { data, error, loading, load } = useGrowthData();
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    void load(range.start_date, range.end_date);
  }, [load, range]);

  const chartLines = [
    { key: "dau", color: "#111", label: t("growth.col_dau") },
    { key: "new_users", color: "#16a34a", label: t("growth.col_new_users") },
  ] as const;

  return (
    <div>
      <DatePresets value={range} onChange={setRange} />
      {error && <p style={shared.errorBanner}>{error}</p>}
      {loading && !data && <p style={shared.loadingText}>Loading...</p>}
      {data && <KPICards data={data} highlight={highlight} />}
      {data && (
        <section style={shared.section}>
          <TrendLineChart data={data.rows} lines={chartLines} xKey="date" />
        </section>
      )}
      <section style={shared.section}>
        <button
          type="button"
          onClick={() => setShowTable(!showTable)}
          style={s.toggleBtn}
        >
          {showTable ? t("common.hide_details") : t("common.show_details")}
        </button>
        {showTable && data && <DailyTable rows={data.rows} />}
      </section>
    </div>
  );
}

// --- KPI Cards ---

function KPICards({
  data,
  highlight,
}: { data: GrowthMetrics; highlight?: string }): React.JSX.Element {
  const { totals } = data;
  return (
    <section style={shared.section}>
      <div style={shared.cardGrid}>
        <Card
          label={t("growth.avg_dau")}
          value={fmt(totals.dau_avg)}
          highlighted={highlight === "dau"}
        />
        <Card
          label={t("growth.new_users")}
          value={fmt(totals.new_users)}
          highlighted={highlight === "new_users"}
        />
        <Card
          label={t("growth.messages")}
          value={fmt(totals.messages)}
          highlighted={highlight === "messages"}
        />
        <Card
          label={t("growth.summary_requests")}
          value={fmt(totals.summary_requests)}
          highlighted={highlight === "summary_requests"}
        />
      </div>
    </section>
  );
}

function Card({
  label,
  value,
  highlighted,
}: { label: string; value: string; highlighted?: boolean }): React.JSX.Element {
  return (
    <div
      style={{
        ...shared.card,
        padding: 20,
        ...(highlighted ? { borderColor: "#2563eb", borderWidth: 2 } : {}),
      }}
    >
      <span style={s.cardLabel}>{label}</span>
      <span style={shared.bigNumber}>{value}</span>
    </div>
  );
}

// --- Daily Table ---

function DailyTable({
  rows,
}: {
  rows: ReadonlyArray<GrowthMetrics["rows"][number]>;
}): React.JSX.Element {
  return (
    <div style={{ marginTop: 12 }}>
      <h3 style={shared.sectionTitle}>{t("growth.daily_breakdown")}</h3>
      <table style={tableStyles.table}>
        <thead>
          <tr>
            <Th>{t("growth.col_date")}</Th>
            <Th>{t("growth.col_dau")}</Th>
            <Th>{t("growth.col_new_users")}</Th>
            <Th>{t("growth.col_messages")}</Th>
            <Th>{t("growth.col_summary_req")}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.date}>
              <Td>{row.date}</Td>
              <Td mono>{fmt(row.dau)}</Td>
              <Td mono>{fmt(row.new_users)}</Td>
              <Td mono>{fmt(row.messages)}</Td>
              <Td mono>{fmt(row.summary_requests)}</Td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <Td colSpan={5}>{t("common.no_data_for_period")}</Td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// --- Hooks ---

function useGrowthData(): {
  data: GrowthMetrics | null;
  error: string | null;
  loading: boolean;
  load: (start: string, end: string) => Promise<void>;
} {
  const [data, setData] = useState<GrowthMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (start: string, end: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        setData(await fetchGrowthMetrics({ start_date: start, end_date: end }));
      } catch (err: unknown) {
        setError(
          err instanceof Error ? err.message : t("common.failed_to_load"),
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, error, loading, load };
}

// --- Styles ---

const s: Record<string, React.CSSProperties> = {
  cardLabel: {
    display: "block",
    fontSize: 13,
    color: "var(--color-text-secondary)",
    fontWeight: 500,
    marginBottom: 4,
  },
  toggleBtn: {
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
    cursor: "pointer",
  },
};
